import moment from "moment";
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import ContainerAnimation from "components/ContainerAnimation";
import { useAuth } from "providers/Auth";
import { RoleDescription } from "types/enums";
import { downloadCnabForDay } from "utils/cnabExport";
import { formatPrice } from "utils/formats";
import {
  fetchRepasseQuotesForDay,
  RepasseQuote,
  REPASSE_BUSINESS_DAYS,
} from "utils/repasse";
import { toast } from "utils/toast";

import * as S from "./styles";

interface ILocationState {
  date: string; // YYYY-MM-DD
  companyId: number;
  professionalId: number;
}

interface CompanyGroup {
  id: number;
  name: string;
  total: number;
  items: RepasseQuote[];
}

const RepasseDetails = () => {
  const [repasses, setRepasses] = useState<RepasseQuote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isExportingCnab, setIsExportingCnab] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = useAuth();

  const state = location.state as ILocationState | null;

  useEffect(() => {
    if (!state?.date) return;
    const { date, companyId, professionalId } = state;

    const controller = new AbortController();

    async function loadRepasses() {
      setIsLoading(true);
      setHasError(false);
      try {
        const data = await fetchRepasseQuotesForDay({
          date,
          companyId,
          professionalId,
          signal: controller.signal,
        });
        setRepasses(data);
        setIsLoading(false);
      } catch (error: any) {
        if (error?.code === "ERR_CANCELED") return;
        console.error(error);
        setHasError(true);
        setIsLoading(false);
      }
    }
    loadRepasses();
    return () => controller.abort();
  }, []);

  if (!state?.date) {
    return <Navigate to="/agenda" />;
  }

  const eventDate = moment(state.date);
  const totalDay = repasses.reduce((sum, r) => sum + r.value, 0);
  const pendingCount = repasses.filter((r) => !r.isPaidOut).length;

  const companyGroups: CompanyGroup[] = Object.values(
    repasses.reduce<Record<number, CompanyGroup>>((acc, r) => {
      const id = r.quote.company?.id ?? 0;
      acc[id] = acc[id] ?? {
        id,
        name: r.quote.company?.name ?? "Sem instituição",
        total: 0,
        items: [],
      };
      acc[id].total += r.value;
      acc[id].items.push(r);
      return acc;
    }, {})
  ).sort((a, b) => b.total - a.total);

  const handleExportCnab = async () => {
    setIsExportingCnab(true);
    try {
      // Um único arquivo de remessa com todos os repasses do dia
      await downloadCnabForDay({
        creditDate: state.date,
        groups: companyGroups.map((group) => ({
          companyId: group.id,
          companyName: group.name,
          items: group.items
            .filter((r) => r.value > 0)
            .map((r) => ({
              quoteId: r.quote.id,
              professionalId: r.quote.professional?.id,
              professionalName: r.quote.professional?.name ?? "",
              value: r.value,
            })),
        })),
      });
    } catch (err) {
      console.error("[CNAB240] ERRO:", err);
      toast.fire({
        icon: "error",
        title: "Erro ao gerar o CNAB 240. Tente novamente.",
      });
    } finally {
      setIsExportingCnab(false);
    }
  };

  return (
    <ContainerAnimation className="content">
      {/* ── Header ── */}
      <S.PageHeader>
        <div className="container">
          <S.Breadcrumb aria-label="Navegação">
            <Link to="/agenda">Calendário</Link>
            <span aria-hidden="true">›</span>
            <span aria-current="page">
              Repasses de {eventDate.format("DD/MM/YYYY")}
            </span>
          </S.Breadcrumb>

          <S.HeaderRow>
            <S.PageTitle>
              <S.BackButton
                onClick={() => navigate(-1)}
                aria-label="Voltar para o calendário"
                title="Voltar"
              >
                <i className="uil uil-arrow-left" aria-hidden="true" />
              </S.BackButton>
              Repasses do dia
              <S.DateBadge>{eventDate.format("DD/MM/YYYY")}</S.DateBadge>
            </S.PageTitle>

            {userRole === RoleDescription.Admin &&
              !isLoading &&
              repasses.length > 0 && (
                <S.CnabButton
                  onClick={handleExportCnab}
                  disabled={isExportingCnab}
                  aria-label="Baixar arquivo CNAB 240 com todos os repasses do dia"
                >
                  {isExportingCnab ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Gerando…
                    </>
                  ) : (
                    <>
                      <i className="uil uil-file-download" aria-hidden="true" />
                      Baixar CNAB 240 do dia
                    </>
                  )}
                </S.CnabButton>
              )}
          </S.HeaderRow>

          <S.RuleHint>
            <i className="uil uil-info-circle" aria-hidden="true" />
            Pagamentos por link ou máquina física são repassados em D+
            {REPASSE_BUSINESS_DAYS} dias úteis (pagamento + split).
          </S.RuleHint>
        </div>
      </S.PageHeader>

      <div className="container pt-3">
        {/* ── Loading ── */}
        {isLoading && (
          <div className="d-flex flex-column gap-3" aria-label="Carregando repasses">
            <S.Skeleton height="4rem" />
            <S.Skeleton height="12rem" />
          </div>
        )}

        {/* ── Erro ── */}
        {!isLoading && hasError && (
          <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
            <i className="uil uil-exclamation-octagon fs-5 flex-shrink-0"></i>
            <span>
              Não foi possível carregar os repasses do dia. Volte ao
              calendário e tente novamente.
            </span>
          </div>
        )}

        {/* ── Summary ── */}
        {!isLoading && !hasError && repasses.length > 0 && (
          <S.SummaryBar aria-label="Resumo do dia">
            <S.SummaryItem>
              <div className="s-label">Total a repassar</div>
              <div className="s-value">{formatPrice(totalDay)}</div>
            </S.SummaryItem>
            <S.SummaryItem>
              <div className="s-label">Orçamentos</div>
              <div className="s-value" style={{ color: "var(--text-primary)" }}>
                {repasses.length}
              </div>
            </S.SummaryItem>
            <S.SummaryItem>
              <div className="s-label">Instituições</div>
              <div className="s-value" style={{ color: "var(--text-primary)" }}>
                {companyGroups.length}
              </div>
            </S.SummaryItem>
            {pendingCount > 0 && (
              <S.SummaryItem>
                <div className="s-label">Aguardando repasse</div>
                <div className="s-value" style={{ color: "#b45309" }}>
                  {pendingCount}
                </div>
              </S.SummaryItem>
            )}
          </S.SummaryBar>
        )}

        {/* ── Empty ── */}
        {!isLoading && !hasError && repasses.length === 0 && (
          <S.EmptyState role="status">
            <i className="uil uil-money-withdraw" aria-hidden="true" />
            <p>Nenhum repasse previsto para esta data.</p>
            <small>
              Os repasses aparecem 2 dias úteis após o pagamento do orçamento.
            </small>
          </S.EmptyState>
        )}

        {/* ── Grupos por instituição ── */}
        {!isLoading &&
          !hasError &&
          companyGroups.map((group) => (
            <S.CompanyCard key={group.id} aria-label={`Repasses de ${group.name}`}>
              <S.CompanyHeader>
                <i className="uil uil-building" aria-hidden="true" />
                <S.CompanyName title={group.name}>{group.name}</S.CompanyName>
                <S.CompanyTotal>{formatPrice(group.total)}</S.CompanyTotal>
              </S.CompanyHeader>

              <div className="table-responsive">
                <S.QuoteTable>
                  <thead>
                    <tr>
                      <th>Orçamento</th>
                      <th>Paciente</th>
                      <th>Profissional</th>
                      <th>Pagamento</th>
                      <th>Status</th>
                      <th className="num">Valor pago</th>
                      <th className="num">Repasse</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((r) => (
                      <tr key={r.quote.id}>
                        <td>
                          <Link
                            to="/orcamentos-profissional/orcamento-detalhes"
                            state={r.quote.id}
                            aria-label={`Ver orçamento #${r.quote.id}`}
                          >
                            #{r.quote.id}
                          </Link>
                        </td>
                        <td>{r.quote.patient?.name ?? "—"}</td>
                        <td>{r.quote.professional?.name ?? "—"}</td>
                        <td>
                          {r.quote.payment?.paymentDate &&
                          r.quote.payment.paymentDate !== "0001-01-01T00:00:00"
                            ? moment(r.quote.payment.paymentDate).format(
                                "DD/MM/YYYY"
                              )
                            : "—"}
                        </td>
                        <td>
                          <S.StatusTag $paid={r.isPaidOut}>
                            <i
                              className={`uil ${
                                r.isPaidOut
                                  ? "uil-check-circle"
                                  : "uil-clock"
                              }`}
                              aria-hidden="true"
                            />
                            {r.isPaidOut ? "Repassado" : "A repassar"}
                          </S.StatusTag>
                        </td>
                        <td className="num">
                          {formatPrice(r.quote.payment.value)}
                        </td>
                        <td className="num repasse-value">
                          {formatPrice(r.value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </S.QuoteTable>
              </div>
            </S.CompanyCard>
          ))}
      </div>
    </ContainerAnimation>
  );
};

export default RepasseDetails;
